import { Category } from '@/material/web/category'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'

export type MaterialApplyTarget = {
  category: ResourceCategoryKey
  folderSegments: string[]
}

const dynamicPortraitCategoryIds = new Set<string>()

/**
 * 登记运行时从素材库接口识别出的立绘类顶级分类。
 * @param topCategoryId 素材库顶级分类 ID。
 */
export function registerMaterialPortraitCategoryId(topCategoryId: string): void {
  const cleanId = String(topCategoryId || '').trim()
  if (cleanId) dynamicPortraitCategoryIds.add(cleanId)
}

/** 运行时登记的立绘类顶级分类（如狗橙、狗橙和朋友们、狗橙和朋友）。 */
export function isDynamicPortraitMaterialCategory(topCategoryId: string): boolean {
  return dynamicPortraitCategoryIds.has(String(topCategoryId || '').trim())
}

/** UI 素材单独走 `oui` 导入流程，不复用普通素材映射目标。 */
export function isUiMaterialCategory(topCategoryId: string): boolean {
  return topCategoryId === Category.UI
}

/** 当前顶级分类是否支持“应用到工程”。 */
export function canApplyMaterialCategory(topCategoryId: string): boolean {
  return isUiMaterialCategory(topCategoryId) || !!getMaterialApplyTarget(topCategoryId)
}

/** 返回普通素材的默认落点；不支持时返回 null，供 UI 做显式判定。 */
export function getMaterialApplyTarget(topCategoryId: string): MaterialApplyTarget | null {
  switch (topCategoryId) {
    case Category.推荐:
    case Category.人物立绘:
    case Category.辅助立绘:
    case Category.偶像区:
    case Category.以爱之名:
      return { category: 'graphic', folderSegments: ['立绘'] }
    case Category.背景:
    case Category.CG:
      return { category: 'graphic', folderSegments: ['背景'] }
    case Category.特效:
      return { category: 'graphic', folderSegments: ['动图'] }
    case Category.音乐:
      return { category: 'audio', folderSegments: ['音乐'] }
    case Category.音效:
      return { category: 'audio', folderSegments: ['音效'] }
    default:
      if (dynamicPortraitCategoryIds.has(String(topCategoryId || '').trim())) {
        return { category: 'graphic', folderSegments: ['立绘'] }
      }
      return null
  }
}

/** 普通素材应用流程使用的严格版本；不支持时抛出业务错误。 */
export function resolveMaterialApplyTarget(topCategoryId: string): MaterialApplyTarget {
  const target = getMaterialApplyTarget(topCategoryId)
  if (!target) {
    throw new Error('当前素材类型暂不支持应用')
  }
  return target
}
